import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { contourOf, pathInContour, type ContourKind } from "../utils/contour";

/**
 * WHICH CONTOUR YOU ARE EDITING, AND THE WAY TO THE OTHER ONE.
 *
 * It sits in the tab bar rather than on one screen, because the contour is a
 * property of everything below it: the same ambiguity that cost us a
 * production edit of free access applies to insights, premium and app
 * settings. One place to read it, one place to change it.
 *
 * WHAT IT REFUSES TO DO. For a section with no declared twin it does NOT say
 * "production" — it names the section and stops. Calling an unmeasured section
 * production would be the same kind of confident wrong sentence this component
 * exists to prevent; a reader can see the id and ask.
 */
const ContourSwitch = ({ project }: { project: string | null }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const contour = contourOf(project);

  if (!project) return null;

  if (!contour) {
    /* No twin is declared for this section. State the fact, claim nothing. */
    return (
      <div className="cms__contour cms__contour--single" title="This product has no separate stage section">
        <span className="cms__contour-label">section</span>
        <span className="cms__contour-name">{project}</span>
      </div>
    );
  }

  const go = (to: ContourKind) => {
    if (to === contour.kind) return;
    const next = pathInContour(location.pathname, to);
    if (next) navigate(next);
  };

  return (
    <div
      className={`cms__contour cms__contour--${contour.kind}`}
      /* The title is the sentence the screen could not say before. */
      title={
        contour.kind === "prod"
          ? "You are editing PRODUCTION — these changes reach buyers"
          : "You are editing STAGE — safe to try things here"
      }
    >
      <button
        type="button"
        className={`cms__contour-btn ${contour.kind === "stage" ? "cms__contour-btn--on" : ""}`}
        onClick={() => go("stage")}
        aria-pressed={contour.kind === "stage"}
      >
        Stage
      </button>
      <button
        type="button"
        className={`cms__contour-btn cms__contour-btn--prod ${contour.kind === "prod" ? "cms__contour-btn--on" : ""}`}
        onClick={() => go("prod")}
        aria-pressed={contour.kind === "prod"}
      >
        Production
      </button>
    </div>
  );
};

export default ContourSwitch;
